import { cn } from '@rocco/utils/cn';
import { useOpenGetMatchedModal } from '~/state/ui-state-get-matched-modal';
import { useDirectoryBrowseTheme } from './directory-browse-theme';

interface DirectoryIntroProps {
    /** Small grey label above the heading, e.g. "Products Library". */
    eyebrow: string;
    /** Editorial intro copy. */
    heading: string;
    /**
     * Active category name (e.g. "Commercial"). On a category page it's shown
     * bold before the heading ("Commercial - {heading}"), mirroring the legacy
     * TitleSection so the category is named even when the description falls back
     * to the generic root copy. Omit at the root.
     */
    categoryPrefix?: string;
    /**
     * Mobile presentation. 'page-lead' (default) treats the copy as quiet meta
     * and hides the Get Matched rail entirely — the grid injections carry that
     * CTA on mobile. 'grid-injection' keeps the pitch prominent with the
     * Get Matched button, hiding only the plus icon. Desktop is identical in
     * both variants.
     */
    variant?: 'page-lead' | 'grid-injection';
    /**
     * Hide the rail's "Can't find it?" caption below md. On desktop the
     * caption annotates the far-right rail; stacked under the statement on
     * mobile the two read as repetition, so hosts whose layout stacks them
     * (the detail pages' closing module) drop it there.
     */
    hideMobileCaption?: boolean;
    /**
     * Rail caption copy. The default names suppliers (the directory grids'
     * audience); the project detail closing swaps in professionals to match
     * the flow its brief opens.
     */
    caption?: string;
}

/**
 * Directory browse intro: eyebrow + editorial heading on the left, and the
 * "Get Matched" prompt + button on the right. Matches the 5.0 category-page
 * design. The buttons open the Get Matched modal, which the revamp browse
 * routes mount (entity-typed) — without a mounted modal (e.g. the legacy
 * browse grid's injections) the click is a no-op and the intro stays
 * presentational.
 */
export const DirectoryIntro = ({
    eyebrow,
    heading,
    categoryPrefix,
    variant = 'page-lead',
    hideMobileCaption = false,
    caption = "Can't find it? Describe what you need and matching suppliers will come to you.",
}: DirectoryIntroProps) => {
    const onGetMatched = useOpenGetMatchedModal();
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const isInjection = variant === 'grid-injection';
    // Mobile lead label: the category name on child pages, otherwise the
    // vertical ("Products Library" → "Products"), homepage "(About)" style.
    const introLabel = categoryPrefix ?? eyebrow.replace(/\s*Library\s*$/i, '');
    // First sentence carries the ink; the remainder reads as the pale tail
    // (the logged-out homepage statement treatment).
    const sentenceBreak = heading.indexOf('. ');
    const statementLead =
        sentenceBreak === -1 ? heading : heading.slice(0, sentenceBreak + 1);
    const statementTail =
        sentenceBreak === -1 ? '' : heading.slice(sentenceBreak + 1);
    // Dark editorial theme (projects): heading flips to white and the Get Matched
    // buttons invert to a white fill; the grey eyebrow/description read on both.
    const buttonClass = isDark
        ? 'bg-white text-[#181818]'
        : 'bg-[#181818] text-white';
    return (
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-10">
            {/* Mobile treats the editorial copy as quiet meta, not a hero —
                small type, tight eyebrow, so the category carousel owns the
                first viewport. Desktop values unchanged via md:. */}
            <div className="flex flex-col gap-2 md:max-w-[60.375rem] md:gap-[2.375rem]">
                {/* Page h1: the category (or vertical) name. A display heading
                    below md (the homepage "Discover" scale); sr-only at md+
                    where the title reads inline in the statement instead. */}
                {!isInjection ? (
                    <h1
                        className={cn(
                            'font-sans text-[3.0326rem] leading-[3.0998rem] font-normal tracking-[-0.1268rem] md:sr-only',
                            isDark ? 'text-white' : 'text-[#181818]',
                        )}
                    >
                        {introLabel}
                    </h1>
                ) : null}
                {/* Desktop eyebrow — the mobile lead uses the parenthesised
                    label inside the statement instead. */}
                <p
                    className={cn(
                        'text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.046875rem] text-[#767676]',
                        !isInjection && 'max-md:hidden',
                    )}
                >
                    {eyebrow}
                </p>
                {isInjection ? (
                    <p
                        className={cn(
                            'text-[1.375rem] leading-[1.3] font-medium tracking-[-0.0515625rem] md:text-[2.0625rem] md:leading-[1.25]',
                            isDark ? 'text-white' : 'text-[#181818]',
                        )}
                    >
                        {heading}
                    </p>
                ) : (
                    /* Logged-out-homepage statement idiom on mobile: inline
                       "(Label)", first sentence in ink, the remainder in the
                       pale grey. Desktop keeps the classic bold-prefix
                       statement untouched. */
                    <p
                        className={cn(
                            'font-sans text-[1.1256rem] leading-[1.2] font-normal tracking-[-0.03em]',
                            'md:text-[2.0625rem] md:leading-[1.25] md:font-medium md:tracking-[-0.0515625rem]',
                            isDark ? 'text-white' : 'text-[#181818]',
                        )}
                    >
                        {categoryPrefix ? (
                            <span className="font-semibold max-md:hidden">
                                {`${categoryPrefix} - `}
                            </span>
                        ) : null}
                        <span>{statementLead}</span>
                        {statementTail ? (
                            <span
                                className={cn(
                                    isDark
                                        ? 'max-md:text-white/60'
                                        : 'max-md:text-[#767676]',
                                )}
                            >
                                {statementTail}
                            </span>
                        ) : null}
                    </p>
                )}
            </div>
            {/* page-lead hides the rail below md: the Get Matched sections
                injected into the grid carry this CTA on mobile, flowing in as
                natural separators. The injection variant keeps it, tightened. */}
            <div
                className={cn(
                    'flex flex-col items-start md:w-[27.8125rem] md:items-end',
                    isInjection
                        ? 'gap-4 md:gap-[2.0625rem]'
                        : 'gap-[2.0625rem] max-md:hidden',
                )}
            >
                <div
                    className={cn(
                        'flex items-center gap-[0.6875rem] md:justify-end',
                        hideMobileCaption && 'max-md:hidden',
                    )}
                >
                    <p className="text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.046875rem] text-[#767676] md:w-[20.0625rem] md:text-right">
                        {caption}
                    </p>
                    <button
                        type="button"
                        onClick={onGetMatched}
                        aria-label="Get Matched"
                        className={cn(
                            'relative flex size-[2.625rem] shrink-0 items-center justify-center rounded-full',
                            // The plus icon is desktop chrome — the labelled
                            // Get Matched button below is the mobile CTA.
                            isInjection && 'max-md:hidden',
                            // Extend the tap target to ≥44px below md via an
                            // invisible pseudo — the visual circle stays 42px.
                            "max-md:before:absolute max-md:before:top-1/2 max-md:before:left-1/2 max-md:before:size-11 max-md:before:-translate-x-1/2 max-md:before:-translate-y-1/2 max-md:before:content-['']",
                            buttonClass,
                        )}
                    >
                        {/* The design's icon is a light "times" cross rotated
                            -45° into a plus — the rotation gives it longer, more
                            elegant arms than a geometric plus. */}
                        <svg
                            viewBox="0 0 23.6048 23.6048"
                            fill="none"
                            aria-hidden="true"
                            className="size-[1.475rem] -rotate-45"
                        >
                            <path
                                d="M13.4718 11.8027L18.5155 6.75893L19.5556 5.71881C19.709 5.56538 19.709 5.31604 19.5556 5.16261L18.4427 4.0497C18.2893 3.89627 18.0399 3.89627 17.8865 4.0497L11.8027 10.1335L5.71881 4.04921C5.56538 3.89578 5.31604 3.89578 5.16261 4.04921L4.04921 5.16212C3.89578 5.31555 3.89578 5.56489 4.04921 5.71832L10.1335 11.8027L4.04921 17.8865C3.89578 18.0399 3.89578 18.2893 4.04921 18.4427L5.16212 19.5556C5.31555 19.709 5.56489 19.709 5.71832 19.5556L11.8027 13.4718L16.8464 18.5155L17.8865 19.5556C18.0399 19.709 18.2893 19.709 18.4427 19.5556L19.5556 18.4427C19.709 18.2893 19.709 18.0399 19.5556 17.8865L13.4718 11.8027Z"
                                fill="currentColor"
                            />
                        </svg>
                    </button>
                </div>
                <button
                    type="button"
                    onClick={onGetMatched}
                    className={cn(
                        'flex h-[3.25rem] w-[11.625rem] items-center justify-center rounded-[1.875rem] px-[1.625rem] py-3 text-[1rem] leading-[1.3] font-medium tracking-[0.02rem]',
                        isDark
                            ? 'bg-white text-[#181818]'
                            : 'bg-[#181818] text-[#f3f3f3]',
                    )}
                >
                    Get Matched
                </button>
            </div>
        </div>
    );
};
